import type { KpiContext } from '../audit/kpiContext';
import type { Role } from '../data/types';
import type { AnswerMap, AnswerValue, TemplateDefinition } from '../audit/template';
import type { TemplateSettings } from '../audit/validateTemplate';
import type { ScoreBreakdown } from '../audit/scoring';
import { idbBackend, idbBlobBackend } from './db';
import type {
  BlobBackend,
  ClosedElsewhereReason,
  LocalAttachment,
  LocalAuditSession,
  LocalAuditStatus,
  LocalNote,
  LocalTaskDraft,
  SessionBackend,
} from './types';

/**
 * Session operations — every mutation is ONE atomic read-modify-write of the
 * whole session record (`backend.update`, a single IndexedDB readwrite
 * transaction), so answers and their dirty-markers commit together (a crash
 * can never leave an answer saved but unmarked for sync, spec §8) and
 * overlapping writers — keystroke saves, the sync engine, another tab —
 * never base a write on a stale read and silently undo each other. (Two
 * tabs editing the SAME question still last-writer-wins.)
 */

export interface StartSessionInput {
  storeId: number;
  storeNumber: string;
  storeName: string;
  templateId: number;
  templateVersion: number;
  templateName: string;
  definition: TemplateDefinition;
  settings: TemplateSettings;
  frequencyDays: number;
  auditorUpn: string;
  /** The signed-in account (not "View as") — see LocalAuditSession.ownerUpn. */
  ownerUpn?: string;
  conductorRole?: Role;
  initialAnswers?: AnswerMap;
  previewOnly?: boolean;
  /** Stable id override (used by the preview surface); default random UUID. */
  localId?: string;
  /** For server-resume: adopt an existing Audits row instead of creating one. */
  serverId?: number;
  responseIds?: Record<string, number>;
  missingAttributes?: string[];
  storeAccountUpn?: string;
  /**
   * Server-resume: the audit's ORIGINAL start (Audits.StartedAt). Without
   * it the adopted copy would claim it started now, and that date would be
   * written back over the server's on submit.
   */
  startedAt?: string;
  /**
   * Server-resume: notes/tasks/files the other device already uploaded,
   * pre-stamped with their server ids so they show here but never re-sync.
   */
  notes?: LocalNote[];
  taskDrafts?: LocalTaskDraft[];
  attachments?: LocalAttachment[];
}

/** What the sync engine reports back after a push (see applySyncResult). */
export type SyncResultUpdate = {
  serverId?: number;
  newResponseIds?: Record<string, number>;
  removedResponseKeys?: string[];
  /** Header pushed while the session had this status → clean if unchanged. */
  headerSyncedStatus?: LocalAuditStatus;
  /** Server ids, or the refusal reason (text) for items the server refused for good. */
  noteServerIds?: Record<string, number | string>;
  taskServerIds?: Record<string, number | string>;
  /** attachmentId → uploaded item id + file URL. */
  attachmentUploads?: Record<string, { serverItemId: number; url: string }>;
  /** Attachments whose local bytes were lost (storage evicted). */
  attachmentMissingBlobs?: string[];
  /** Attachment id → why the server refused it for good. */
  attachmentRejected?: Record<string, string>;
} & (
  | { clearedQuestions?: undefined; syncedAnswers?: undefined }
  | {
      /** Questions whose ops all succeeded… */
      clearedQuestions: string[];
      /** …and the answers those ops were built from. */
      syncedAnswers: AnswerMap;
    }
);

/** Same stored answer? Values are plain JSON (structured-cloned). */
function sameAnswer(a: AnswerValue | undefined, b: AnswerValue | undefined): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** Copy `ids` from `from` onto `base` (an id absent in `from` is cleared). */
function mergeAnswers(base: AnswerMap, from: AnswerMap, ids: string[]): AnswerMap {
  const merged = { ...base };
  for (const id of ids) {
    const value = from[id];
    if (value === undefined) delete merged[id];
    else merged[id] = value;
  }
  return merged;
}

export function createSessionStore(
  backend: SessionBackend = idbBackend,
  blobs: BlobBackend = idbBlobBackend,
) {
  async function startSession(input: StartSessionInput): Promise<LocalAuditSession> {
    const now = new Date().toISOString();
    const initialAnswers = input.initialAnswers ?? {};
    const adoptingServerRow = input.serverId !== undefined;
    const session: LocalAuditSession = {
      localId: input.localId ?? crypto.randomUUID(),
      serverId: input.serverId ?? null,
      storeId: input.storeId,
      storeNumber: input.storeNumber,
      storeName: input.storeName,
      templateId: input.templateId,
      templateVersion: input.templateVersion,
      templateName: input.templateName,
      definition: input.definition,
      settings: input.settings,
      frequencyDays: input.frequencyDays,
      auditorUpn: input.auditorUpn,
      ...(input.ownerUpn ? { ownerUpn: input.ownerUpn } : {}),
      ...(input.conductorRole ? { conductorRole: input.conductorRole } : {}),
      status: 'in_progress',
      startedAt: input.startedAt ?? now,
      answers: initialAnswers,
      // Pre-filled answers (store-attribute gates) must sync like any other;
      // server-resumed answers are already synced by definition.
      dirtyQuestions: adoptingServerRow ? [] : Object.keys(initialAnswers),
      headerDirty: !adoptingServerRow,
      responseIds: input.responseIds ?? {},
      updatedAt: now,
      ...(input.previewOnly ? { previewOnly: true } : {}),
      ...(input.missingAttributes?.length
        ? { missingAttributes: input.missingAttributes }
        : {}),
      ...(input.storeAccountUpn ? { storeAccountUpn: input.storeAccountUpn } : {}),
      ...(input.notes?.length ? { notes: input.notes } : {}),
      ...(input.taskDrafts?.length ? { taskDrafts: input.taskDrafts } : {}),
      ...(input.attachments?.length ? { attachments: input.attachments } : {}),
    };
    await backend.put(session);
    return session;
  }

  async function getSession(localId: string): Promise<LocalAuditSession | undefined> {
    return backend.get(localId);
  }

  async function listSessions(): Promise<LocalAuditSession[]> {
    const all = await backend.list();
    return all.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }

  /**
   * The synchronous-feeling write path: the form passes its whole answers
   * map plus the question(s) it changed; ONLY those keys are merged into
   * the stored answers (absent = cleared) and marked dirty. Overlapping
   * calls are safe — they commit in call order and each touches only its
   * own keys, so neither a stale in-page map nor a second tab holding the
   * same audit can wipe other questions' answers.
   */
  async function saveAnswers(
    localId: string,
    answers: AnswerMap,
    changedQuestionId: string | string[],
  ): Promise<LocalAuditSession> {
    const changed = Array.isArray(changedQuestionId)
      ? changedQuestionId
      : [changedQuestionId];
    const updatedAt = new Date().toISOString();
    return mutate(localId, (session) =>
      // A save that lands after submit/cancel/expiry/takeover changes
      // nothing: closed copies never push answers again, and a dirty flag
      // there would sit pending forever.
      session.status !== 'in_progress'
        ? session
        : {
            ...session,
            answers: mergeAnswers(session.answers, answers, changed),
            dirtyQuestions: [...new Set([...session.dirtyQuestions, ...changed])],
            updatedAt,
          },
    );
  }

  /** Append a note (create-only in v1); syncs once the audit header exists. */
  async function addNote(
    localId: string,
    note: {
      questionId: string | null;
      sampleIndex?: number;
      text: string;
      author: string;
    },
  ): Promise<LocalAuditSession> {
    const record: LocalNote = {
      id: crypto.randomUUID(),
      questionId: note.questionId,
      ...(note.sampleIndex !== undefined ? { sampleIndex: note.sampleIndex } : {}),
      text: note.text,
      author: note.author,
      createdAt: new Date().toISOString(),
    };
    return mutate(localId, (session) => ({
      ...session,
      notes: [...(session.notes ?? []), record],
      updatedAt: record.createdAt,
    }));
  }

  /** Queue a quick-created task; the Tasks row is created by the sync engine. */
  async function addTaskDraft(
    localId: string,
    draft: {
      questionId: string | null;
      sampleIndex?: number;
      title: string;
      description: string;
      assignedTo: string;
      dueDate: string;
      createdBy: string;
      proofRequired?: boolean;
    },
  ): Promise<LocalAuditSession> {
    const record: LocalTaskDraft = {
      id: crypto.randomUUID(),
      ...draft,
      createdAt: new Date().toISOString(),
    };
    return mutate(localId, (session) => ({
      ...session,
      taskDrafts: [...(session.taskDrafts ?? []), record],
      updatedAt: record.createdAt,
    }));
  }

  /**
   * Queue an attachment: blob first, then metadata — a crash between the
   * two leaves an orphan blob (harmless), never metadata without bytes.
   */
  async function addAttachment(
    localId: string,
    meta: {
      questionId: string;
      sampleIndex?: number;
      kind: 'photo' | 'pdf';
      mimeType: string;
      uploadedBy: string;
    },
    blob: Blob,
  ): Promise<LocalAuditSession> {
    if (!(await backend.get(localId))) {
      throw new Error(`No local audit session '${localId}'`);
    }
    const id = crypto.randomUUID();
    const ext = meta.kind === 'pdf' ? 'pdf' : 'jpg';
    const record: LocalAttachment = {
      id,
      questionId: meta.questionId,
      ...(meta.sampleIndex !== undefined ? { sampleIndex: meta.sampleIndex } : {}),
      kind: meta.kind,
      // Deterministic name → upload retries overwrite instead of duplicating.
      filename: `${meta.questionId}-${id.slice(0, 8)}.${ext}`,
      mimeType: meta.mimeType,
      size: blob.size,
      uploadedBy: meta.uploadedBy,
      createdAt: new Date().toISOString(),
    };
    await blobs.put(id, blob);
    return mutate(localId, (session) => ({
      ...session,
      attachments: [...(session.attachments ?? []), record],
      updatedAt: record.createdAt,
    }));
  }

  /**
   * A sample entry was removed: renumber notes/tasks/attachments pointing
   * at later entries, and demote items of the removed entry to
   * question-level. Local view only — already-synced rows keep the index
   * they were created with (create-only lists; rare, accepted edge).
   */
  async function remapSampleItems(
    localId: string,
    questionId: string,
    removedIndex: number,
  ): Promise<LocalAuditSession> {
    const remap = <T extends { questionId: string | null; sampleIndex?: number }>(
      items: T[] | undefined,
    ): T[] | undefined =>
      items?.map((item) => {
        if (item.questionId !== questionId || item.sampleIndex === undefined) {
          return item;
        }
        if (item.sampleIndex === removedIndex) {
          const { sampleIndex: _dropped, ...rest } = item;
          return rest as T;
        }
        if (item.sampleIndex > removedIndex) {
          return { ...item, sampleIndex: item.sampleIndex - 1 };
        }
        return item;
      });
    const updatedAt = new Date().toISOString();
    return mutate(localId, (session) => ({
      ...session,
      ...(session.notes ? { notes: remap(session.notes)! } : {}),
      ...(session.taskDrafts ? { taskDrafts: remap(session.taskDrafts)! } : {}),
      ...(session.attachments
        ? { attachments: remap(session.attachments)! }
        : {}),
      updatedAt,
    }));
  }

  /** Remove a queued attachment (only before it has uploaded). */
  async function removeAttachment(
    localId: string,
    attachmentId: string,
  ): Promise<LocalAuditSession> {
    const updatedAt = new Date().toISOString();
    // The uploaded-check runs inside the transaction: an upload landing
    // concurrently can't slip past it.
    const next = await mutate(localId, (session) => {
      const target = (session.attachments ?? []).find((a) => a.id === attachmentId);
      if (target?.serverItemId !== undefined) {
        throw new Error('Attachment already uploaded — it can no longer be removed here.');
      }
      return {
        ...session,
        attachments: (session.attachments ?? []).filter((a) => a.id !== attachmentId),
        updatedAt,
      };
    });
    await blobs.delete(attachmentId);
    return next;
  }

  /** Read an attachment's bytes (for thumbnails and upload). */
  async function getAttachmentBlob(attachmentId: string): Promise<Blob | undefined> {
    return blobs.get(attachmentId);
  }

  /** Finalize locally: score, lock, queue for sync (works fully offline). */
  async function markSubmitted(
    localId: string,
    breakdown: ScoreBreakdown,
  ): Promise<LocalAuditSession> {
    const now = new Date().toISOString();
    return mutate(localId, (session) =>
      // A cancelled/expired/closed-elsewhere copy can't be submitted; the
      // caller checks the returned status.
      session.status !== 'in_progress'
        ? session
        : {
            ...session,
            status: 'submitted',
            submittedAt: now,
            score: breakdown.score,
            sectionScores: breakdown.sectionScores,
            criticalFailed: breakdown.criticalFailed,
            headerDirty: true,
            updatedAt: now,
          },
    );
  }

  /**
   * Close an in-progress audit on this device: 'cancelled' (the auditor
   * chose to) or 'expired' (72h after start). Works offline — the header
   * carries the new status to the server on the next sync; pending
   * answers, notes, tasks and files still upload so the record is
   * complete. No-op unless the audit is still in progress.
   */
  async function closeSession(
    localId: string,
    status: 'cancelled' | 'expired',
  ): Promise<LocalAuditSession> {
    const now = new Date().toISOString();
    return mutate(localId, (session) =>
      session.status !== 'in_progress'
        ? session
        : { ...session, status, closedAt: now, headerDirty: true, updatedAt: now },
    );
  }

  /**
   * Another device continued (or closed) this audit. This copy stops: its
   * unsynced ANSWERS are dropped (pushing them would overwrite the newer
   * work) and counted so the UI can say so; the header is never pushed
   * again. Additive items — notes, tasks, files — still upload.
   */
  async function markClosedElsewhere(
    localId: string,
    reason: ClosedElsewhereReason,
  ): Promise<LocalAuditSession> {
    const now = new Date().toISOString();
    return mutate(localId, (session) =>
      session.status === 'closed_elsewhere'
        ? session
        : {
            ...session,
            status: 'closed_elsewhere',
            closedElsewhere: {
              reason,
              detectedAt: now,
              discardedAnswers: session.dirtyQuestions.length,
            },
            dirtyQuestions: [],
            headerDirty: false,
            updatedAt: now,
          },
    );
  }

  /**
   * Called by the sync engine after successful pushes. The engine pushed a
   * SNAPSHOT it read earlier (a network round trip ago); anything the
   * auditor changed since then must stay dirty, so clears are checked
   * against what was actually pushed.
   */
  async function applySyncResult(
    localId: string,
    update: SyncResultUpdate,
  ): Promise<LocalAuditSession | undefined> {
    return backend.update(localId, (session) => {
      // Clean only if the answer is still what was uploaded — a newer edit
      // (typing on while the batch was in flight) has to upload too.
      const cleared = new Set(
        (update.clearedQuestions ?? []).filter((q) =>
          sameAnswer(session.answers[q], update.syncedAnswers?.[q]),
        ),
      );
      const responseIds = { ...session.responseIds, ...update.newResponseIds };
      for (const key of update.removedResponseKeys ?? []) {
        delete responseIds[key];
      }
      const withServerId = <T extends { id: string; serverId?: number; rejected?: string }>(
        items: T[] | undefined,
        ids: Record<string, number | string> | undefined,
      ): T[] | undefined =>
        ids && items
          ? items.map((item) => {
              const v = ids[item.id];
              if (typeof v === 'number') return { ...item, serverId: v };
              if (typeof v === 'string') return { ...item, rejected: v };
              return item;
            })
          : items;
      let attachments = session.attachments;
      if (attachments && (update.attachmentUploads || update.attachmentMissingBlobs || update.attachmentRejected)) {
        const uploads = update.attachmentUploads ?? {};
        const lost = new Set(update.attachmentMissingBlobs ?? []);
        const rejected = update.attachmentRejected ?? {};
        attachments = attachments.map((a) => {
          const uploaded = uploads[a.id];
          if (uploaded) {
            return { ...a, serverItemId: uploaded.serverItemId, url: uploaded.url };
          }
          const reason = rejected[a.id];
          if (reason !== undefined) return { ...a, missingBlob: true, rejected: reason };
          return lost.has(a.id) ? { ...a, missingBlob: true } : a;
        });
      }
      return {
        ...session,
        serverId: update.serverId ?? session.serverId,
        dirtyQuestions: session.dirtyQuestions.filter((q) => !cleared.has(q)),
        responseIds,
        // Pushed as in_progress but submitted/cancelled meanwhile? The newer
        // status still has to reach the server.
        headerDirty:
          update.headerSyncedStatus !== undefined &&
          update.headerSyncedStatus === session.status
            ? false
            : session.headerDirty,
        ...(withServerId(session.notes, update.noteServerIds)
          ? { notes: withServerId(session.notes, update.noteServerIds)! }
          : {}),
        ...(withServerId(session.taskDrafts, update.taskServerIds)
          ? { taskDrafts: withServerId(session.taskDrafts, update.taskServerIds)! }
          : {}),
        ...(attachments ? { attachments } : {}),
      };
    });
  }

  /**
   * Repair a start date (sessions adopted from the server before the
   * startedAt fix carried the adoption time instead of the real start).
   * Local metadata only — nothing is marked dirty.
   */
  async function correctStartedAt(
    localId: string,
    startedAt: string,
  ): Promise<LocalAuditSession | undefined> {
    return backend.update(localId, (session) =>
      session.startedAt === startedAt ? session : { ...session, startedAt },
    );
  }

  /** Store the KPI hints snapshot (B8) — local metadata only, nothing is marked dirty. */
  async function setKpiContext(localId: string, kpiContext: KpiContext): Promise<LocalAuditSession | undefined> {
    return backend.update(localId, (session) => ({ ...session, kpiContext }));
  }

  async function deleteSession(localId: string): Promise<void> {
    // Free the attachment bytes too — sessions own their blobs.
    const session = await backend.get(localId);
    for (const a of session?.attachments ?? []) {
      await blobs.delete(a.id).catch(() => undefined);
    }
    await backend.delete(localId);
  }

  /** Atomic update of a session that must exist. */
  async function mutate(
    localId: string,
    fn: (current: LocalAuditSession) => LocalAuditSession,
  ): Promise<LocalAuditSession> {
    const next = await backend.update(localId, fn);
    if (!next) throw new Error(`No local audit session '${localId}'`);
    return next;
  }

  return {
    startSession,
    getSession,
    listSessions,
    saveAnswers,
    addNote,
    addTaskDraft,
    addAttachment,
    removeAttachment,
    remapSampleItems,
    getAttachmentBlob,
    markSubmitted,
    closeSession,
    markClosedElsewhere,
    applySyncResult,
    correctStartedAt,
    setKpiContext,
    deleteSession,
  };
}
